import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import api from './services/api';
import Login from './components/Login';
import Navigation from './components/Navigation';
import UserManagement from './components/UserManagement';
import Profile from './components/Profile';
import AccountSelector from './components/AccountSelector';
import BalanceDisplay from './components/BalanceDisplay';
import StatementExport from './components/StatementExport';
import TransactionList from './components/TransactionList';
import './App.css';

function Dashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [statements, setStatements] = useState([]); // All transactions
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
    to: new Date()
  });
  const [direction, setDirection] = useState(''); // '' for all, 'INCOMING' for incoming, 'OUTGOING' for outgoing
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [exporting, setExporting] = useState(null); // Track which format is currently exporting

  // Sort all statements by date
  const sortedStatements = useMemo(() => {
    if (!statements || statements.length === 0) return [];
    
    return [...statements].sort((a, b) => {
      const dateA = new Date(a.finishedAt || a.createdAt);
      const dateB = new Date(b.finishedAt || b.createdAt);
      
      if (sortOrder === 'asc') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  }, [statements, sortOrder]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/api/accounts');
      setAccounts(response.data);
      if (response.data.length > 0) {
        setSelectedAccount(response.data[0]);
      }
    } catch (err) {
      setError('Failed to load accounts');
      console.error(err);
    }
  };

  const fetchAccountData = useCallback(async () => {
    if (!selectedAccount) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch balance
      const balanceResponse = await api.get(`/api/accounts/${selectedAccount.id}/balance`);
      if (balanceResponse.data.status === 'SUCCESS') {
        setBalance(balanceResponse.data.payload);
      }

      // Fetch all statements (no pagination parameters)
      // Format dates properly for the API - expects ISO format without milliseconds
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);
      
      // Set to start and end of day in local time, then convert to ISO
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      
      const params = {
        dateFrom: fromDate.toISOString().split('.')[0],
        dateTill: toDate.toISOString().split('.')[0]
      };
      
      console.log('Date range:', params.dateFrom, 'to', params.dateTill);
      
      // Add direction filter if not 'all'
      if (direction) {
        params.direction = direction;
      }
      
      const statementsResponse = await api.get(`/api/accounts/${selectedAccount.id}/statements`, { params });
      
      if (statementsResponse.data.status === 'SUCCESS') {
        const responseData = statementsResponse.data.payload || [];
        console.log('API returned', responseData.length, 'total transactions');
        
        // Set all statements at once
        setStatements(responseData);
      }
    } catch (err) {
      setError('Failed to load account data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, dateRange, direction]);

  // Fetch available accounts on mount
  useEffect(() => {
    fetchAccounts();
  }, []);


  // Fetch data when account, date range, or direction changes
  useEffect(() => {
    if (selectedAccount) {
      fetchAccountData();
    }
  }, [selectedAccount, dateRange, direction, fetchAccountData]);


  const handleExport = async (format) => {
    if (!selectedAccount) return;

    setExporting(format);
    try {
      const params = {
        dateFrom: dateRange.from.toISOString().split('T')[0],
        dateTill: dateRange.to.toISOString().split('T')[0],
        currency: 'EUR'
      };

      // Include direction filter if set
      if (direction && direction !== 'all') {
        params.direction = direction;
      }

      const response = await api.get(
        `/api/accounts/${selectedAccount.id}/export/${format}`,
        {
          params,
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      let extension;
      let filename;
      if (format === 'excel') {
        extension = 'xls';
        filename = `statement_${selectedAccount.accountId}_${params.dateFrom}_${params.dateTill}.${extension}`;
      } else if (format === 'csv-detailed') {
        extension = 'csv';
        filename = `detailed_statement_${selectedAccount.accountId}_${params.dateFrom}_${params.dateTill}.${extension}`;
      } else {
        extension = format;
        filename = `statement_${selectedAccount.accountId}_${params.dateFrom}_${params.dateTill}.${extension}`;
      }
      link.setAttribute('download', filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to export ${format.toUpperCase()}`);
      console.error(err);
    } finally {
      setExporting(null);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagement />;
      case 'profile':
        return <Profile />;
      case 'dashboard':
      default:
        return (
          <div className="dashboard-container">
            <div className="combined-controls-section">
              <AccountSelector
                accounts={accounts}
                selectedAccount={selectedAccount}
                onAccountChange={setSelectedAccount}
              />

              {selectedAccount && (
                <div className="balance-section">
                  <BalanceDisplay balance={balance} loading={loading} />
                </div>
              )}

              <div className="filters-and-export">
                <div className="date-range-selector">
                  <label>
                    From:
                    <input
                      type="date"
                      value={dateRange.from.toISOString().split('T')[0]}
                      onChange={(e) => setDateRange({ ...dateRange, from: new Date(e.target.value) })}
                    />
                  </label>
                  <label>
                    To:
                    <input
                      type="date"
                      value={dateRange.to.toISOString().split('T')[0]}
                      onChange={(e) => setDateRange({ ...dateRange, to: new Date(e.target.value) })}
                    />
                  </label>
                  <label>
                    Direction:
                    <select
                      value={direction}
                      onChange={(e) => setDirection(e.target.value)}
                      className="direction-filter"
                    >
                      <option value="">All</option>
                      <option value="INCOMING">Incoming</option>
                      <option value="OUTGOING">Outgoing</option>
                    </select>
                  </label>
                  <button onClick={fetchAccountData} disabled={loading}>
                    {loading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
                
                <div className="export-row">
                  <StatementExport
                    onExport={handleExport}
                    disabled={!selectedAccount || loading}
                    exporting={exporting}
                  />
                </div>
              </div>
            </div>

            {selectedAccount && (
              <>

                <div className="transactions-section">
                  <TransactionList 
                    statements={sortedStatements}
                    loading={loading}
                    sortOrder={sortOrder}
                    onSortChange={setSortOrder}
                  />
                </div>
              </>
            )}
          </div>
        );
    }
  };

  return (
    <div className="App">
      <aside className="App-sidebar">
        <div className="sidebar-header">
          <div className="logo-container">
            <img 
              alt="Logo" 
              loading="lazy" 
              width="65" 
              height="65" 
              decoding="async" 
              data-nimg="1" 
              src="/logo.svg" 
              style={{
                color: 'transparent', 
                width: 'calc(65px * var(--wm, 1))', 
                height: 'calc(65px * var(--hm, 1))'
              }}
            />
            <h1>MONEY</h1>
          </div>
        </div>
        <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />
      </aside>
      
      <div className="App-content">
        <main className="App-main">
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {renderContent()}
        </main>
      </div>
    </div>
  );
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return <Dashboard />;
}

function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWithAuth;